import { Ellipsis } from '@/components/ui/ellipsis';
import { cn } from '@/utils/classUtils';
import { FileText } from 'lucide-react';
import { type FC, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

/** Shared shell: default `shadow-sm`; hover slightly stronger than `shadow`, softer than `shadow-md`. */
const DOCUMENT_REFERENCE_BUTTON_SHELL =
  'inline-flex items-center min-w-0 max-w-full rounded border border-neutral-200 bg-white text-neutral-900 shadow-sm transition-shadow hover:shadow-[0_3px_5px_-1px_rgba(0,0,0,0.1),0_2px_3px_-2px_rgba(0,0,0,0.08)] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:ring-offset-2';

/** Default / list contexts: mini-medium (text-xs font-medium). */
const SIZE_DEFAULT_CLASS = 'gap-1.5 px-2 py-[3px] text-xs font-medium';

/** Update detail header only: paragraph-small-medium (text-sm font-medium), pill still hugs content. */
const SIZE_DETAIL_CLASS = 'gap-2 px-2.5 py-1 text-sm font-medium';

export const DOCUMENT_REFERENCE_BUTTON_CLASS = cn(
  DOCUMENT_REFERENCE_BUTTON_SHELL,
  SIZE_DEFAULT_CLASS
);

export type DocumentReferenceButtonSize = 'default' | 'detail';

export const DocumentReferenceButton: FC<{
  title: string;
  workspaceUname: string;
  /** When omitted, renders the same visuals without navigation (no document to link to). */
  referenceId?: string;
  className?: string;
  /** When false, renders non-interactive styling (e.g. current document page). */
  interactive?: boolean;
  /** `detail` uses paragraph-small-medium for the prominent update header only. */
  size?: DocumentReferenceButtonSize;
}> = ({
  title,
  workspaceUname,
  referenceId,
  className,
  interactive = true,
  size = 'default',
}) => {
  const navigate = useNavigate();
  const path =
    referenceId && workspaceUname
      ? `/workspace/${workspaceUname}/regulatory-watch/references/${referenceId}`
      : undefined;

  const handleClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (interactive && path) {
      navigate(path, {
        state: {
          backPath: `/workspace/${workspaceUname}/regulatory-watch/all-documents`,
          backLabel: 'My references',
        },
      });
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  const sizeClass = size === 'detail' ? SIZE_DETAIL_CLASS : SIZE_DEFAULT_CLASS;
  const iconClass = size === 'detail' ? 'h-4 w-4 shrink-0' : 'h-3 w-3 shrink-0';

  if (!interactive || !referenceId || !path) {
    return (
      <span
        className={cn(
          DOCUMENT_REFERENCE_BUTTON_SHELL,
          sizeClass,
          'cursor-default',
          className
        )}
        aria-current={interactive === false ? 'page' : undefined}
      >
        <FileText className={cn(iconClass, 'text-neutral-900')} />
        <Ellipsis lineClamp={1} hasTooltip className="min-w-0 text-left">
          {title}
        </Ellipsis>
      </span>
    );
  }

  return (
    <button
      type="button"
      className={cn(
        DOCUMENT_REFERENCE_BUTTON_SHELL,
        sizeClass,
        'cursor-pointer text-left',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <FileText className={cn(iconClass, 'text-neutral-900')} />
      <Ellipsis lineClamp={1} hasTooltip className="min-w-0">
        {title}
      </Ellipsis>
    </button>
  );
};
